import { Controller } from "@hotwired/stimulus"

// Generic data-frame field-patcher. Listens for `mercure:data` (dispatched
// by <mercure-data-source>) and patches any element on the page marked:
//
//   <div data-model="fb_instagram_account" data-id="178…">
//     <span data-field="followers_count" data-field-format="number">…</span>
//   </div>
//
// For each key in the frame's `data` hash it sets the matching [data-field]
// descendant (or the marked root itself). Attach once at the layout level
// (data-controller="data-frame") so any page with marked elements updates
// live. Rich updates (list insert/remove, toasts) use dedicated controllers
// that also listen for `mercure:data`.
export default class extends Controller {
  connect() {
    this.onData = this.handle.bind(this)
    document.addEventListener("mercure:data", this.onData)
    console.log("[data-frame] patcher connected")
  }

  disconnect() {
    document.removeEventListener("mercure:data", this.onData)
  }

  handle(event) {
    const frame = event.detail
    if (!frame || !frame._model || frame._id == null || !frame.data) return

    // Attribute-equality filter (not a built selector) — avoids CSS.escape
    // and is injection-safe regardless of frame values.
    const id = String(frame._id)
    const roots = Array.from(document.querySelectorAll("[data-model][data-id]")).filter(
      (el) => el.getAttribute("data-model") === frame._model && el.getAttribute("data-id") === id,
    )
    if (roots.length === 0) return

    roots.forEach((root) => this.patch(root, frame.data))
    console.log(`[data-frame] patched ${roots.length} el(s) for ${frame._model}#${frame._id}`)
  }

  patch(root, data) {
    Object.entries(data).forEach(([key, value]) => {
      if (value === null || value === undefined) return
      if (root.getAttribute("data-field") === key) this.setValue(root, value)
      Array.from(root.querySelectorAll("[data-field]"))
        .filter((el) => el.getAttribute("data-field") === key)
        .forEach((el) => this.setValue(el, value))
    })
  }

  setValue(el, value) {
    const out =
      el.getAttribute("data-field-format") === "number" && typeof value === "number"
        ? value.toLocaleString()
        : value
    if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.tagName === "SELECT") {
      el.value = out
    } else {
      el.textContent = String(out)
    }
  }
}
